import React, {useState} from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import AppButton from './button';
import RedactedChip from './redacted-chip';
import DeclassificationEffect from './declassification-effect';
import {sourceCheckMeta} from '../util/source-check';

const CHANGE_TYPE_CHIP_CLASSES = {
  CREATE: 'chip-green',
  UPDATE: 'chip-orange',
  DELETE: 'chip-red',
};

const STATUS_CHIP_CLASSES = {
  PENDING: 'chip-orange',
  APPROVED: 'chip-green',
  REJECTED: 'chip-red',
  IMPLEMENTED: 'chip-blue',
};

const ACTION_LABELS = {
  APPROVED: 'Approve',
  REJECTED: 'Reject',
};

// Human-readable labels for diff field names (raw camelCase otherwise).
const FIELD_LABELS = {
  columnName: 'Column name',
  dataType: 'Data type',
  maxLength: 'Max length',
  isNullable: 'Nullable',
  defaultValue: 'Default value',
  columnKey: 'Key constraint',
  classification: 'Classification',
  isUnique: 'Unique constraint',
  isUnsigned: 'Unsigned',
  isZerofill: 'Zerofill',
  isBinary: 'Binary',
  isAutoIncrement: 'Auto increment',
};

// Boolean-style constraint flags rendered as Yes/No in the diff table.
const BOOLEAN_FLAG_FIELDS = ['isUnique', 'isUnsigned', 'isZerofill', 'isBinary', 'isAutoIncrement'];

function fieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

// Normalize boolean-like field values to a consistent human-readable format.
function normalizeFieldValue(field, value) {
  if (field === 'isNullable') {
    if (value === '1' || value === 'YES' || value === true || value === 1) return 'YES';
    if (value === '0' || value === 'NO' || value === false || value === 0) return 'NO';
  }
  // The stored values are lowercase enum members; ClassificationPanel shows them title-cased. Two
  // surfaces describing the same field should not disagree about its name (slice 5o-b).
  if (field === 'classification') {
    if (value === 'internal') return 'Internal';
    if (value === 'confidential') return 'Confidential';
  }
  if (BOOLEAN_FLAG_FIELDS.indexOf(field) !== -1) {
    if (value === '1' || value === 1 || value === true || value === 'YES') return 'Yes';
    if (value === '0' || value === 0 || value === false || value === 'NO') return 'No';
  }
  return value;
}

// Change-history timeline (#595). Maps a merged timeline event (DRIFT / DECISION /
// RECON from the backend) to a dot color + human title. Pure — exported for tests.
const TIMELINE_COLORS = {
  blue: '#1565c0', green: '#2e7d32', red: '#c62828',
  amber: '#e65100', grey: '#9e9e9e', indigo: '#3f51b5',
};

function buildTimelineItem(ev) {
  if (!ev) return {color: TIMELINE_COLORS.grey, title: '', detail: ''};
  const by = ev.actor ? ` by ${ev.actor}` : '';
  if (ev.kind === 'DRIFT') {
    return {color: TIMELINE_COLORS.blue, title: `Drift detected · ${ev.changeType || 'Change'}`, detail: ev.detail || ''};
  }
  if (ev.kind === 'DECISION') {
    const map = {
      APPROVED: [TIMELINE_COLORS.green, 'Approved'],
      REJECTED: [TIMELINE_COLORS.red, 'Rejected'],
      REOPENED: [TIMELINE_COLORS.amber, 'Reopened'],
    };
    const [color, label] = map[ev.action] || [TIMELINE_COLORS.grey, ev.action || 'Decision'];
    return {color, title: `${label}${by}`, detail: ev.detail || ''};
  }
  if (ev.kind === 'RECON') {
    const colorMap = {
      REVERTED: TIMELINE_COLORS.green, STILL_IN_DB: TIMELINE_COLORS.amber,
      SOURCE_UNREACHABLE: TIMELINE_COLORS.grey, NEW_VALUE: TIMELINE_COLORS.red,
    };
    const via = ev.actor ? ` (${ev.actor})` : '';
    return {color: colorMap[ev.result] || TIMELINE_COLORS.grey, title: `Live check${via}`, detail: ev.detail || ''};
  }
  return {color: TIMELINE_COLORS.grey, title: ev.title || '', detail: ev.detail || ''};
}

const ChangeRequestDetailSheet = ({
  open,
  changeRequest,
  changes,
  timeline = [],
  isLoading,
  onClose,
  onNavigate,
  onAction,
  onRollback,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const [comments, setComments] = useState('');

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAction = (action) => {
    setSelectedAction(action);
  };

  const handleSubmit = () => {
    if (!selectedAction) return;
    if (onAction) {
      onAction(selectedAction, comments);
      setSelectedAction(null);
      setComments('');
    }
  };

  const renderDiff = (cr) => {
    if (!cr) return null;
    let proposed = null;
    try {
      proposed = typeof cr.proposed_changes === 'object' ?
        cr.proposed_changes :
        JSON.parse(cr.proposed_changes || '{}');
    } catch (e) {
      return <p className="diff-error">Unable to parse diff data.</p>;
    }

    // Slice 2k. A redacted proposed_changes arrives as `{"redacted":true}` — the object sentinel
    // SERIALISED, because proposed_changes is a JSON column and db/mysql.js pins jsonStrings:true,
    // so it is a string on the wire and the parse above is what turns it back into an object.
    // diff_summary is TEXT, so it arrives as the bare string '[redacted]'. Without this branch the
    // drawer renders the sentinel to the user as if it were the diff. The chip says the same thing
    // on purpose.
    //
    // This comment previously claimed the object sentinel arrived as an object. It did not, so
    // JSON.parse threw at :148 and the catch returned before this line was ever reached — the chip
    // below was dead code from slice 2k until the sentinel was taught to stay parseable.
    const withheld = cr.redactedFields || [];
    const diffRedacted = withheld.includes('proposed_changes') || withheld.includes('diff_summary');

    const columnDiffs = proposed.columnDiffs || [];
    // Slice 5o-b. Entity-level field diffs — today only classification, raised manually. Guarded on
    // columnDiffs being empty so no REFRESH payload can reach this branch: those carry their changes
    // per column and already render in the table below.
    //
    // This is why the slice exists at all. Before it, a reclassification payload matched neither
    // outcome of this function, so the approver was asked to approve a VISIBILITY change against
    // "No detailed diff available."
    const fieldDiffs = (columnDiffs.length === 0 && Array.isArray(proposed.fieldDiffs)) ? proposed.fieldDiffs : [];
    if (columnDiffs.length === 0 && fieldDiffs.length === 0 && !proposed.previous && !proposed.current) {
      return (
        <p className="diff-empty" data-testid="cr-diff-empty">
          {diffRedacted ?
            <RedactedChip fields={withheld} data-testid="cr-diff-redacted" /> :
            (cr.diff_summary || 'No detailed diff available.')}
        </p>
      );
    }

    return (
      <div>
        {diffRedacted && (
          <p className="diff-summary">
            <RedactedChip fields={withheld} data-testid="cr-diff-redacted" />
          </p>
        )}
        {!diffRedacted && cr.diff_summary && (
          <p className="diff-summary">
            {cr.diff_summary}
          </p>
        )}

        {/*
          S5's "Effect if approved". A reclassification diff says `Confidential → Internal`; it does
          not say how far that reaches, and that is the fact the decision turns on. The calculation
          existed only in scripts/access/classification-visibility.js — run out of band, before the
          flip — so the approver was asked to widen access without being told by how much.

          Only for a widening. `internal → confidential` is monotone-safe and needs no approval at
          all (§7.1), so there is nothing to weigh.
        */}
        {fieldDiffs.some((fd) => fd.field === 'classification' &&
          String(fd.newValue).toLowerCase() === 'internal') && cr.request_id && (
          <DeclassificationEffect requestId={cr.request_id} />
        )}

        {fieldDiffs.length > 0 && (
          <Table className="data-table" size="small" data-testid="cc-drawer-classification-diff">
            <TableHead>
              <TableRow>
                <TableCell><b>Field</b></TableCell>
                <TableCell><b>Previous</b></TableCell>
                <TableCell><b>Proposed</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fieldDiffs.map((fd, i) => (
                <TableRow key={i} className="diff-row-modified">
                  <TableCell>{fieldLabel(fd.field)}</TableCell>
                  <TableCell className="diff-cell-old">{normalizeFieldValue(fd.field, fd.oldValue) || '—'}</TableCell>
                  <TableCell>{normalizeFieldValue(fd.field, fd.newValue) || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {columnDiffs.length > 0 && (
          <Table className="data-table" size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Column</b></TableCell>
                <TableCell><b>Change</b></TableCell>
                <TableCell><b>Previous</b></TableCell>
                <TableCell><b>Current</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columnDiffs.map((cd, i) => (
                <React.Fragment key={i}>
                  {cd.type === 'COLUMN_ADDED' && (
                    <TableRow className="diff-row-added">
                      <TableCell>{cd.columnName}</TableCell>
                      <TableCell>Added</TableCell>
                      <TableCell className="diff-cell-empty">—</TableCell>
                      <TableCell>
                        {cd.new ? `${cd.new.dataType}${cd.new.maxLength ? `(${cd.new.maxLength})` : ''}` : ''}
                      </TableCell>
                    </TableRow>
                  )}
                  {cd.type === 'COLUMN_REMOVED' && (
                    <TableRow className="diff-row-removed">
                      <TableCell>{cd.columnName}</TableCell>
                      <TableCell>Removed</TableCell>
                      <TableCell>
                        {cd.old ? `${cd.old.dataType}${cd.old.maxLength ? `(${cd.old.maxLength})` : ''}` : ''}
                      </TableCell>
                      <TableCell className="diff-cell-empty">—</TableCell>
                    </TableRow>
                  )}
                  {cd.type === 'COLUMN_MODIFIED' && (cd.fieldDiffs || []).map((fd, j) => (
                    <TableRow key={j} className="diff-row-modified">
                      <TableCell>{cd.columnName}</TableCell>
                      <TableCell>{fieldLabel(fd.field)} changed</TableCell>
                      <TableCell className="diff-cell-old">{normalizeFieldValue(fd.field, fd.oldValue) || '—'}</TableCell>
                      <TableCell>{normalizeFieldValue(fd.field, fd.newValue) || '—'}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  };

  if (!changeRequest) return null;

  const changeType = changeRequest.change_type || 'UPDATE';
  const status = changeRequest.status || 'PENDING';
  const isImplemented = status.toUpperCase() === 'IMPLEMENTED';
  const isPending = status.toUpperCase() === 'PENDING';

  // Find current change request index
  const currentIndex = changes ? changes.findIndex((c) =>
    c.request_id === changeRequest.request_id,
  ) : -1;

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (changes?.length - 1);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="change-detail-sheet"
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
      }}
      PaperProps={{
        style: {width: isExpanded ? '50%' : '40%', minWidth: '500px'},
      }}
    >
      <div className="detail-sheet-container">
        {/* Header */}
        <div className="detail-sheet-header">
          <div className="detail-sheet-header-content">
            <div className="detail-sheet-title">
              <div className="title-with-type">
                <h2>{changeRequest.object_name || 'Change Request'}</h2>
                <div className="status-chips">
                  <Chip
                    label={status.split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}
                    size="small"
                    className={`status-chip ${STATUS_CHIP_CLASSES[status] || 'chip-grey'}`}
                  />
                </div>
              </div>
            </div>
            <div className="detail-sheet-actions">
              <IconButton
                onClick={() => onNavigate && onNavigate('prev')}
                size="small"
                disabled={!hasPrevious}
                title="Previous change request"
              >
                <i className="material-icons">chevron_left</i>
              </IconButton>
              <IconButton
                onClick={() => onNavigate && onNavigate('next')}
                size="small"
                disabled={!hasNext}
                title="Next change request"
              >
                <i className="material-icons">chevron_right</i>
              </IconButton>
              <IconButton onClick={onClose} size="small" title="Close">
                <i className="material-icons">close</i>
              </IconButton>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="detail-sheet-content">
          <div className="detail-sheet-info">
            {/* Object Info */}
            <div className="info-item-row">
              <span className="info-label">Object</span>
              <span className="info-value">{changeRequest.object_name || 'N/A'}</span>
            </div>

            <div className="info-item-row" data-testid="cc-drawer-schema">
              <span className="info-label">Schema</span>
              <span className="info-value">{changeRequest.schema_name || 'N/A'}</span>
            </div>

            {changeRequest.instance_name && (
              <div className="info-item-row" data-testid="cc-drawer-instance">
                <span className="info-label">Data Dictionary</span>
                <span className="info-value">{changeRequest.instance_name}</span>
              </div>
            )}

            {changeRequest.column_name && (
              <div className="info-item-row">
                <span className="info-label">Column / Field</span>
                <span className="info-value">{changeRequest.column_name}</span>
              </div>
            )}

            <div className="info-item-row">
              <span className="info-label">Source</span>
              <span className="info-value text-capitalize">
                {(changeRequest.change_source || 'MANUAL').toLowerCase()}
              </span>
            </div>

            <div className="info-item-row">
              <span className="info-label">Change Type</span>
              <span className="info-value text-capitalize">
                {(changeType || '—').toLowerCase()}
              </span>
            </div>

            <div className="info-item-row" data-testid="cc-drawer-sourcecheck">
              <span className="info-label">Source Check</span>
              <span className="info-value">
                <Chip
                  label={sourceCheckMeta(changeRequest).label}
                  size="small"
                  className={`status-chip ${sourceCheckMeta(changeRequest).cls}`}
                />
              </span>
            </div>

            <div className="info-item-row">
              <span className="info-label">Requested By</span>
              <span className="info-value">{changeRequest.requested_by_name || changeRequest.requested_by || '—'}</span>
            </div>

            <div className="info-item-row">
              <span className="info-label">Requested At</span>
              <span className="info-value">
                {changeRequest.requested_at ? new Date(changeRequest.requested_at).toLocaleString() : '—'}
              </span>
            </div>

            <Divider />

            {/* Diff Section */}
            <div className="detail-section">
              <h3>Changes</h3>
              {renderDiff(changeRequest)}
            </div>

            {/* Slice 5o-b. The drawer never showed `justification`, which was survivable while every
                request was machine-raised and the sentence was generated. A manual proposal makes it
                MANDATORY and human — the only thing an approver has to judge a reclassification
                against — so leaving it unrendered would make the requirement pointless. */}
            {changeRequest.justification && (
              <div className="detail-section" data-testid="cc-drawer-justification">
                <h3>Reason given</h3>
                {(changeRequest.redactedFields || []).includes('justification') ? (
                  <RedactedChip fields={['justification']} data-testid="cr-justification-redacted" />
                ) : (
                  <p className="diff-summary">{changeRequest.justification}</p>
                )}
              </div>
            )}

            {/* Change history timeline (#595): drift → decisions → live checks, merged */}
            <div className="detail-section" data-testid="cc-drawer-timeline">
              <h3>Change history</h3>
              {timeline && timeline.length > 0 ? (
                <ul style={{listStyle: 'none', margin: 0, padding: '0 0 0 8px', borderLeft: '2px solid #eceff1'}}>
                  {timeline.map((ev, idx) => {
                    const item = buildTimelineItem(ev);
                    return (
                      <li
                        key={idx}
                        data-testid={'cc-drawer-timeline-item-' + idx}
                        style={{position: 'relative', padding: '0 0 18px 20px'}}
                      >
                        <span style={{position: 'absolute', left: -7, top: 4, width: 12, height: 12, borderRadius: '50%', background: item.color, border: '2px solid #fff'}} />
                        <div style={{fontWeight: 600, fontSize: 13, color: '#37474f'}}>{item.title}</div>
                        {/* Slice 2k: the backend redacts the EMITTED events, so a withheld comment or
                    drift summary arrives as ev.detail === '[redacted]' with ev.redactedFields
                    naming it. Show the chip rather than the sentinel string. */}
                        {(ev.redactedFields || []).includes('detail') ?
                          <div style={{marginTop: 2}}>
                            <RedactedChip fields={ev.redactedFields} data-testid="cr-timeline-redacted" />
                          </div> :
                          (item.detail && <div style={{fontSize: 12.5, color: '#546e7a', marginTop: 2}}>{item.detail}</div>)}
                        <div style={{fontSize: 11.5, color: '#90a4ae', marginTop: 2}}>
                          {ev.at ? new Date(ev.at).toLocaleString() : ''}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="diff-empty">No change history yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Footer with Actions — only PENDING requests are actionable.
            IMPLEMENTED is terminal (#595): no rollback from the detail sheet. */}
        {isPending && (
          <div className="detail-sheet-footer">
            <div className="footer-actions-container">
              {/* Approve/Reject and the comment form are mutually exclusive:
                  picking an action swaps the buttons out for the form; Cancel
                  swaps back to the initial state. */}
              {!selectedAction ? (
                <div className="action-buttons">
                  {Object.keys(ACTION_LABELS).map((action) => (
                    <AppButton
                      key={action}
                      data-testid={'workflow-detail-' + action.toLowerCase()}
                      label={ACTION_LABELS[action]}
                      className={action === 'APPROVED' ? 'btn btn-green' : 'btn btn-red'}
                      onClick={() => handleAction(action)}
                    />
                  ))}
                </div>
              ) : (
                <div className="action-form">
                  <TextField
                    label="Comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    className="form-field"
                  />
                  <div className="form-actions">
                    <AppButton
                      label="Cancel"
                      className="btn btn-default"
                      onClick={() => {
                        setSelectedAction(null);
                        setComments('');
                      }}
                    />
                    <AppButton
                      data-testid="workflow-detail-submit"
                      label={`Submit ${ACTION_LABELS[selectedAction]}`}
                      className="btn btn-action"
                      onClick={handleSubmit}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export {fieldLabel, normalizeFieldValue, buildTimelineItem};
export default ChangeRequestDetailSheet;
